import { Navigate, useParams } from 'react-router-dom'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { DetailHeader } from '@/components/common/DetailHeader'
import { useCustomer } from './hooks'
import { customerDisplayName } from './types'
import { CustomerTypeBadge } from './components/CustomerTypeBadge'
import { CustomerStatusBadge } from './components/CustomerStatusBadge'
import { CustomerGeneralTab } from './components/CustomerGeneralTab'
import { CustomerContactsTab } from './components/CustomerContactsTab'
import { CustomerAddressesTab } from './components/CustomerAddressesTab'

export function CustomerDetailPage() {
  const params = useParams<{ id: string }>()
  const id = params.id ? Number(params.id) : NaN
  const valid = Number.isFinite(id) && id > 0
  const { data, isLoading, isError } = useCustomer(valid ? id : null)

  if (!valid) return <Navigate to="/clients" replace />

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 p-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32" />
        <Skeleton className="h-64" />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col gap-2 rounded-lg border bg-card p-6 m-4">
        <h2 className="font-medium">Nie udało się wczytać klienta</h2>
        <p className="text-sm text-muted-foreground">
          Sprawdź czy klient istnieje i czy masz uprawnienie partners.customer.read.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col w-full">
      <DetailHeader
        title={customerDisplayName(data)}
        subtitle={<span className="font-mono">{data.code ?? `C-${data.id}`}</span>}
        badges={
          <>
            <CustomerTypeBadge type={data.type} />
            <CustomerStatusBadge active={data.isActive} />
          </>
        }
      />

      <Tabs defaultValue="general" className="p-4">
        <TabsList>
          <TabsTrigger value="general">Główne</TabsTrigger>
          <TabsTrigger value="contacts">
            Kontakty ({data.phones.length + data.emails.length})
          </TabsTrigger>
          <TabsTrigger value="addresses">
            Adresy ({data.addresses.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <CustomerGeneralTab customer={data} />
        </TabsContent>
        <TabsContent value="contacts">
          <CustomerContactsTab customer={data} />
        </TabsContent>
        <TabsContent value="addresses">
          <CustomerAddressesTab customer={data} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
